import {
  createAssociatedTokenAccountIdempotentInstruction,
  getAssociatedTokenAddress,
  TOKEN_PROGRAM_ID,
} from "@solana/spl-token";
import { LAMPORTS_PER_SOL, PublicKey, TransactionInstruction } from "@solana/web3.js";
import { WithdrawalTicket } from "./dataStructures";
import { createBurnWithdrawalPayload } from "./payloads";

export interface BurnWithdrawalAccounts {
  staker: PublicKey;
  vrtMint: PublicKey;
  supportedTokenMint: PublicKey;
  vault: PublicKey;
  vaultConfig: PublicKey;
  vaultProgram: PublicKey;
  feeWallet: PublicKey;
  restakingFeeWallet: PublicKey;
}

/** Shared ATAs for a vault/mint pair — included once per transaction. */
export async function buildSharedBurnWithdrawalAtaInstructions(
  accounts: BurnWithdrawalAccounts
): Promise<TransactionInstruction[]> {
  const {
    staker,
    vrtMint,
    supportedTokenMint,
    vault,
    feeWallet,
    restakingFeeWallet,
  } = accounts;

  const stakerSupportedTokenAta = await getAssociatedTokenAddress(
    supportedTokenMint,
    staker
  );
  const feeWalletVrtAta = await getAssociatedTokenAddress(vrtMint, feeWallet, true);
  const vaultSupportedTokenAta = await getAssociatedTokenAddress(
    supportedTokenMint,
    vault,
    true
  );
  const restakingFeeWalletVrtAta = await getAssociatedTokenAddress(
    vrtMint,
    restakingFeeWallet,
    true
  );

  return [
    createAssociatedTokenAccountIdempotentInstruction(
      staker,
      stakerSupportedTokenAta,
      staker,
      supportedTokenMint
    ),
    createAssociatedTokenAccountIdempotentInstruction(
      staker,
      feeWalletVrtAta,
      feeWallet,
      vrtMint
    ),
    createAssociatedTokenAccountIdempotentInstruction(
      staker,
      vaultSupportedTokenAta,
      vault,
      supportedTokenMint
    ),
    createAssociatedTokenAccountIdempotentInstruction(
      staker,
      restakingFeeWalletVrtAta,
      restakingFeeWallet,
      vrtMint
    ),
  ];
}

/** Per-ticket ticket ATA + burn instruction. */
export async function buildBurnWithdrawalInstructionsForTicket(
  accounts: BurnWithdrawalAccounts,
  ticketInfo: WithdrawalTicket
): Promise<TransactionInstruction[]> {
  const {
    staker,
    vrtMint,
    supportedTokenMint,
    vault,
    vaultConfig,
    vaultProgram,
    feeWallet,
    restakingFeeWallet,
  } = accounts;

  const base = new PublicKey(ticketInfo.base);
  const seeds = [
    Buffer.from("vault_staker_withdrawal_ticket"),
    vault.toBuffer(),
    base.toBuffer(),
  ];
  const [ticket] = PublicKey.findProgramAddressSync(seeds, vaultProgram);

  const ticketVrtAta = await getAssociatedTokenAddress(vrtMint, ticket, true);
  const stakerSupportedTokenAta = await getAssociatedTokenAddress(
    supportedTokenMint,
    staker
  );
  const feeWalletVrtAta = await getAssociatedTokenAddress(vrtMint, feeWallet, true);
  const vaultSupportedTokenAta = await getAssociatedTokenAddress(
    supportedTokenMint,
    vault,
    true
  );
  const restakingFeeWalletVrtAta = await getAssociatedTokenAddress(
    vrtMint,
    restakingFeeWallet,
    true
  );

  const createTicketVrtAtaIx = createAssociatedTokenAccountIdempotentInstruction(
    staker,
    ticketVrtAta,
    ticket,
    vrtMint
  );

  const burnWithdrawalTicketIx = new TransactionInstruction({
    keys: [
      { pubkey: vaultConfig, isSigner: false, isWritable: false },
      { pubkey: vault, isSigner: false, isWritable: true },
      { pubkey: vaultSupportedTokenAta, isSigner: false, isWritable: true },
      { pubkey: vrtMint, isSigner: false, isWritable: true },
      { pubkey: staker, isSigner: true, isWritable: true },
      { pubkey: stakerSupportedTokenAta, isSigner: false, isWritable: true },
      { pubkey: ticket, isSigner: false, isWritable: true },
      { pubkey: ticketVrtAta, isSigner: false, isWritable: true },
      { pubkey: feeWalletVrtAta, isSigner: false, isWritable: true },
      { pubkey: restakingFeeWalletVrtAta, isSigner: false, isWritable: true },
      { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
      { pubkey: new PublicKey("11111111111111111111111111111111"), isSigner: false, isWritable: false },
      { pubkey: vaultProgram, isSigner: false, isWritable: true },
    ],
    programId: vaultProgram,
    data: createBurnWithdrawalPayload(ticketInfo.vrtAmount * LAMPORTS_PER_SOL),
  });

  return [createTicketVrtAtaIx, burnWithdrawalTicketIx];
}

export async function buildBatchBurnWithdrawalInstructions(
  accounts: BurnWithdrawalAccounts,
  tickets: WithdrawalTicket[]
): Promise<TransactionInstruction[]> {
  if (tickets.length === 0) return [];

  const ixs = await buildSharedBurnWithdrawalAtaInstructions(accounts);
  for (const ticket of tickets) {
    const ticketIxs = await buildBurnWithdrawalInstructionsForTicket(accounts, ticket);
    ixs.push(...ticketIxs);
  }
  return ixs;
}
